/**
 * Base Plugin class for all optimization plugins
 * Provides standard interface and performance tracking
 */
export class Plugin {
    constructor(config = {}) {
        this.name = this.constructor.name;
        this.enabled = config.enabled !== false;
        this.config = config;
        // Performance tracking
        this.metrics = {
            initTime: 0,
            executeTime: 0,
            elementCount: 0,
            errors: 0
        };
        // Memory-efficient element tracking
        this.processedElements = new WeakSet();
    }
    /**
     * Clean up resources used by the plugin
     * Called when the plugin is being removed or disabled
     */
    async cleanup() {
        // Default cleanup - can be overridden
        this.processedElements = new WeakSet();
        this.metrics = {
            initTime: 0,
            executeTime: 0,
            elementCount: 0,
            errors: 0
        };
    }
    /**
     * Get plugin metrics for performance analysis
     */
    getMetrics() {
        return {
            name: this.name,
            enabled: this.enabled,
            ...this.metrics,
            impact: this.measureImpact()
        };
    }
    /**
     * Measure the plugin's performance impact
     * Can be overridden by specific plugins
     */
    measureImpact() {
        return {
            totalTime: this.metrics.initTime + this.metrics.executeTime,
            elementsProcessed: this.metrics.elementCount,
            avgTimePerElement: this.metrics.elementCount > 0
                ? (this.metrics.executeTime / this.metrics.elementCount).toFixed(2)
                : 0
        };
    }
    /**
     * Check if an element has already been processed
     */
    isProcessed(element) {
        return this.processedElements.has(element);
    }
    /**
     * Mark an element as processed
     */
    markProcessed(element) {
        this.processedElements.add(element);
        this.metrics.elementCount++;
    }
    /**
     * Log a message with plugin context
     */
    log(message, level = 'info') {
        const prefix = `[${this.name}]`;
        switch (level) {
            case 'error':
                console.error(prefix, message);
                this.metrics.errors++;
                break;
            case 'warn':
                console.warn(prefix, message);
                break;
            case 'debug':
                if (this.config.debug) {
                    console.log(prefix, message);
                }
                break;
            default:
                console.log(prefix, message);
        }
    }
    /**
     * Utility method to batch process elements efficiently
     */
    async batchProcess(elements, batchSize = 5, processor) {
        const batches = [];
        for (let i = 0; i < elements.length; i += batchSize) {
            batches.push(elements.slice(i, i + batchSize));
        }
        for (const batch of batches) {
            await new Promise((resolve) => {
                if ('requestIdleCallback' in window) {
                    requestIdleCallback(() => {
                        processor(batch);
                        resolve();
                    }, { timeout: 50 });
                }
                else {
                    setTimeout(() => {
                        processor(batch);
                        resolve();
                    }, 0);
                }
            });
        }
    }
}
/**
 * Performance-optimized plugin base class
 * Includes built-in batching and efficient DOM operations
 */
export class OptimizedPlugin extends Plugin {
    constructor(config) {
        super(config);
        this.batchSize = config.batchSize || 5;
        this.selector = config.selector || '';
        this._targetElements = null;
    }
    async execute() {
        const startTime = performance.now();
        try {
            // Get target elements
            const elements = this.getTargetElements();
            if (elements.length === 0) {
                this.log('No elements to process', 'debug');
                return;
            }
            this.log(`Processing ${elements.length} elements`, 'debug');
            // Process in batches
            await this.processBatches(elements);
        }
        catch (error) {
            this.log(`Error during execution: ${error.message}`, 'error');
            throw error;
        }
        finally {
            // Record execution time
            this.metrics.executeTime = performance.now() - startTime;
            // Report to global metrics if available
            if (window.__OPTIMIZER_METRICS__) {
                window.__OPTIMIZER_METRICS__.pluginTimes = window.__OPTIMIZER_METRICS__.pluginTimes || {};
                window.__OPTIMIZER_METRICS__.pluginTimes[this.name] = this.metrics.executeTime;
            }
        }
    }
    /**
     * Get elements to process, filtering out already processed ones
     */
    getTargetElements() {
        if (!this._targetElements) {
            const allElements = this.selector
                ? Array.from(document.querySelectorAll(this.selector))
                : [];
            this._targetElements = allElements.filter(el => !this.isProcessed(el));
        }
        return this._targetElements;
    }
    /**
     * Process elements in batches during idle time
     */
    async processBatches(elements) {
        await this.batchProcess(elements, this.batchSize, (batch) => {
            // Batch DOM reads
            const data = batch.map(el => this.readElementData(el));
            // Batch DOM writes
            data.forEach((d, i) => {
                if (d && batch[i]) {
                    this.writeElementData(batch[i], d);
                    this.markProcessed(batch[i]);
                }
            });
        });
    }
}
//# sourceMappingURL=plugin-interface.js.map